import { DocumentationItemConfigurationV2, DocumentationPageV1, groupBy } from "@supernova-studio/model";
import { VersionRoomDocsPage, VersionRoomDocsPageGroup } from "../../../yjs/version-room/types";
import { LocalDocsPage, LocalDocsPageGroup } from "../../../sync/types";
import { DTODocumentationGroupV2 } from "../../dto";
import {
  documentationItemConfigurationToDTOV2,
  getDtoDefaultItemConfigurationV2,
} from "./documentation-item-configuration-v2-to-dto";
import { applyPrivacyConfigurationToNestedItems } from "./documentation-page-to-dto-utils";

type DocsPage = LocalDocsPage | VersionRoomDocsPage;
type DocsPageGroup = LocalDocsPageGroup | VersionRoomDocsPageGroup;

// The fact that DTO conversion is located here instead of main backend code is due to the fact
// that we store page and group data in YJS documents in the same way as they are stored in the database.
// Therefore, we need to expose this conversion to the client so that it can consume data from YJS documents.
//
// Please do not put more DTO conversion here unless you know what you're doing.

export function elementGroupsToDocumentationGroupDTOV2(
  groups: DocsPageGroup[],
  pages: (DocumentationPageV1 | DocsPage)[]
): DTODocumentationGroupV2[] {
  const childrenIdsMap = calculateChildrenIdsMapV2(pages, groups);

  return groups.map(group => elementGroupToDocumentationGroupStructureDTOV2(group, childrenIdsMap));
}

export function elementGroupsToDocumentationGroupFixedConfigurationDTOV2(
  groups: DocsPageGroup[],
  pages: (DocumentationPageV1 | DocsPage)[]
): DTODocumentationGroupV2[] {
  const childrenIdsMap = calculateChildrenIdsMapV2(pages, groups);

  const { groups: fixedGroups } = applyPrivacyConfigurationToNestedItems<
    DocumentationPageV1 | DocsPage,
    DocsPageGroup,
    DocumentationItemConfigurationV2
  >(pages, groups, getDtoDefaultItemConfigurationV2);

  return fixedGroups.map(group => elementGroupToDocumentationGroupStructureDTOV2(group, childrenIdsMap));
}

function elementGroupToDocumentationGroupStructureDTOV2(
  group: DocsPageGroup,
  childrenIdsMap: Map<string, string[]>
): DTODocumentationGroupV2 {
  const childrenIds = childrenIdsMap.get(group.persistentId) ?? [];

  if (!group.shortPersistentId) {
    throw new Error(`Short persistent id is required for docs groups, group id: ${group.persistentId}`);
  }

  return {
    id: "to_be_removed",
    designSystemVersionId: group.designSystemVersionId,
    persistentId: group.persistentId,
    slug: group.slug,
    userSlug: group.userSlug,
    createdAt: group.createdAt,
    updatedAt: group.updatedAt,
    title: group.meta.name,
    configuration: documentationItemConfigurationToDTOV2(group?.data?.configuration),
    childrenIds: childrenIds,
    isRoot: !group.parentPersistentId,
    groupBehavior: group.data?.behavior ?? "Group",
    shortPersistentId: group.shortPersistentId,
    type: "Group",
  };
}

function calculateChildrenIdsMapV2(
  elements: { persistentId: string; parentPersistentId: string; sortOrder: number }[],
  groups: DocsPageGroup[]
): Map<string, string[]> {
  const byParentId = groupBy([...elements, ...groups], e => e.parentPersistentId);

  const childrenIdsMap = new Map<string, string[]>();

  for (const [parentPersistentId, children] of byParentId) {
    if (!parentPersistentId) continue;
    children.sort((lhs, rhs) => lhs.sortOrder - rhs.sortOrder);

    childrenIdsMap.set(
      parentPersistentId,
      children.map(c => c.persistentId)
    );
  }

  return childrenIdsMap;
}
